import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LoginPage from "./pages/LoginPage";
import TechnicianDashboard from "./pages/TechnicianDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SecretaryDashboard from "./pages/SecretaryDashboard";
import InterventionForm from "./pages/InterventionForm";
import InterventionDetail from "./pages/InterventionDetail";
import ReportEditor from "./pages/ReportEditor";
import Planning from "./pages/Planning";
import UserManagement from "./pages/UserManagement";
import DonneursOrdre from "./pages/DonneursOrdre";
import ArchPlanner from "./pages/ArchPlanner";
import DessinSchema from "./pages/DessinSchema";
import Statistics from "./pages/Statistics";
import SearchPage from "./pages/SearchPage";
import Invoices from "./pages/Invoices";
import Activity from "./pages/Activity";
import ActivityTracker from "./components/ActivityTracker";
import VisioClient from "./pages/VisioClient";
import VisioTechnician from "./pages/VisioTechnician";
import VisioRatings from "./pages/VisioRatings";
import VisioHub from "./pages/VisioHub";
import SplashScreen from "./components/SplashScreen";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const defaultRoutes = {
      admin: "/admin",
      secretary: "/secretary",
      technician: "/dashboard"
    };
    return <Navigate to={defaultRoutes[user.role] || "/dashboard"} replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }
  
  if (user) {
    const defaultRoutes = {
      admin: "/admin",
      secretary: "/secretary",
      technician: "/dashboard"
    };
    return <Navigate to={defaultRoutes[user.role] || "/dashboard"} replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SplashScreen />
        <BrowserRouter>
          <ActivityTracker />
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={["technician", "admin"]}>
                <TechnicianDashboard />
              </ProtectedRoute>
            } />
            <Route path="/intervention/new" element={
              <ProtectedRoute allowedRoles={["technician", "admin", "secretary"]}>
                <InterventionForm />
              </ProtectedRoute>
            } />
            <Route path="/intervention/new/:missionId" element={
              <ProtectedRoute allowedRoles={["technician", "admin", "secretary"]}>
                <InterventionForm />
              </ProtectedRoute>
            } />
            <Route path="/intervention/:id" element={
              <ProtectedRoute>
                <InterventionDetail />
              </ProtectedRoute>
            } />
            <Route path="/visio" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <VisioHub />
              </ProtectedRoute>
            } />
            <Route path="/visio/:token" element={<VisioClient />} />
            <Route path="/visio/session/:sessionId" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <VisioTechnician />
              </ProtectedRoute>
            } />
            <Route path="/visio-ratings" element={
              <ProtectedRoute allowedRoles={["admin", "secretary"]}>
                <VisioRatings />
              </ProtectedRoute>
            } />
            <Route path="/intervention/:id/edit-report" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <ReportEditor />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/secretary" element={
              <ProtectedRoute allowedRoles={["secretary"]}>
                <SecretaryDashboard />
              </ProtectedRoute>
            } />
            <Route path="/planning" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <Planning />
              </ProtectedRoute>
            } />
            <Route path="/donneurs-ordre" element={
              <ProtectedRoute allowedRoles={["admin", "secretary"]}>
                <DonneursOrdre />
              </ProtectedRoute>
            } />
            <Route path="/arch-planner" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <ArchPlanner />
              </ProtectedRoute>
            } />
            <Route path="/dessin-schema" element={
              <ProtectedRoute allowedRoles={["admin", "secretary", "technician"]}>
                <DessinSchema />
              </ProtectedRoute>
            } />
            <Route path="/statistics" element={
              <ProtectedRoute allowedRoles={["admin", "secretary"]}>
                <Statistics />
              </ProtectedRoute>
            } />
            <Route path="/search" element={
              <ProtectedRoute allowedRoles={["admin", "secretary"]}>
                <SearchPage />
              </ProtectedRoute>
            } />
            <Route path="/factures" element={
              <ProtectedRoute allowedRoles={["admin", "secretary"]}>
                <Invoices />
              </ProtectedRoute>
            } />
            <Route path="/activite" element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <Activity />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
